import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowRight, MapPin, Award, Trophy, Target } from 'lucide-react';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div className="relative order-2 lg:order-1">
              <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_fd406e50-e045-4358-8502-edcfe539605e/artifacts/eo556y8r_IMG_2903.jpeg" 
                  alt="Trent Tunke - Professional Roping Coach"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Trophy className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-bold text-foreground">2013 Canadian</p>
                    <p className="text-sm text-muted-foreground">Reserve Champion</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="order-1 lg:order-2">
              <div className="flex items-center gap-2 text-muted-foreground mb-4">
                <MapPin className="w-4 h-4" />
                <span>Medicine Hat, Alberta</span>
              </div>
              <h1 className="font-display font-black text-4xl md:text-6xl tracking-tight uppercase text-foreground leading-none mb-6">
                Trent Tunke
              </h1>
              <p className="font-serif text-xl text-muted-foreground leading-relaxed mb-6">
                Co-owner of Elevated Roping Media Inc. and leader of the company's coaching and athlete development programs.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-8">
                With more than two decades of competitive experience, Trent is a numerous Canadian Finals Rodeo qualifier, the 2013 Canadian Reserve Champion, and the 2010 CCA Finals Champion. He has also qualified for major World Series roping events and continues to compete at a high level, bringing current, real-world performance insight into every aspect of his coaching and leadership.
              </p>
              <div className="flex items-center gap-4">
                <Link to="/signup">
                  <Button 
                    size="lg" 
                    className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-8 py-6 font-bold uppercase tracking-wider"
                  >
                    Get Started
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section className="py-16 bg-primary text-primary-foreground">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="font-display font-bold text-4xl mb-2">20+</p>
              <p className="text-primary-foreground/80 text-sm uppercase tracking-wide">Years Competing</p>
            </div>
            <div>
              <p className="font-display font-bold text-4xl mb-2">CFR</p>
              <p className="text-primary-foreground/80 text-sm uppercase tracking-wide">Multiple Qualifier</p>
            </div>
            <div>
              <p className="font-display font-bold text-4xl mb-2">2013</p>
              <p className="text-primary-foreground/80 text-sm uppercase tracking-wide">Reserve Champion</p>
            </div>
            <div>
              <p className="font-display font-bold text-4xl mb-2">2010</p>
              <p className="text-primary-foreground/80 text-sm uppercase tracking-wide">CCA Finals Champion</p>
            </div>
          </div>
        </div>
      </section>

      {/* Background Section */}
      <section className="py-16 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display font-bold text-2xl md:text-4xl tracking-tight uppercase text-foreground mb-8">
            Coaching & Development
          </h2>
          
          <div className="space-y-6 text-foreground leading-relaxed">
            <p>
              Over the past 12 years, Trent has spent winters in Arizona training and competing, working alongside and coaching ropers from beginner to advanced levels. This experience has given him a unique perspective on athlete development—understanding what it takes to progress at every stage, from learning the fundamentals to competing at the highest levels.
            </p>
            <p>
              He has hosted numerous team roping schools and clinics, helping athletes develop technical skills, competitive confidence, and long-term success in the sport. His coaching approach combines hands-on instruction with the kind of real-world insight that only comes from actively competing.
            </p>
            <p>
              As co-owner of Elevated Roping Media Inc., Trent plays a central role in building and leading the company's coaching programs, combining his competitive background, teaching experience, and passion for the sport to help ropers progress at every stage of their journey.
            </p>
          </div>
        </div>
      </section>

      {/* Credentials */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display font-bold text-2xl md:text-4xl tracking-tight uppercase text-foreground mb-8">
            Competitive Achievements
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Trophy className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-display font-bold text-lg uppercase">Championships</h3>
                </div>
                <ul className="space-y-2 text-muted-foreground">
                  <li>• 2013 Canadian Reserve Champion</li>
                  <li>• 2010 CCA Finals Champion</li>
                  <li>• World Series Roping Qualifier</li>
                </ul>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-secondary/10 flex items-center justify-center">
                    <Award className="w-5 h-5 text-secondary" />
                  </div>
                  <h3 className="font-display font-bold text-lg uppercase">Qualifications</h3>
                </div>
                <ul className="space-y-2 text-muted-foreground">
                  <li>• Numerous Canadian Finals Rodeo Qualifier</li>
                  <li>• Major World Series Roping Events</li>
                  <li>• Active High-Level Competitor</li>
                </ul>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                    <Target className="w-5 h-5 text-accent" />
                  </div>
                  <h3 className="font-display font-bold text-lg uppercase">Coaching</h3>
                </div>
                <ul className="space-y-2 text-muted-foreground">
                  <li>• 12+ Years Coaching Experience</li>
                  <li>• Beginner to Advanced Athletes</li>
                  <li>• Team Roping Schools & Clinics</li>
                </ul>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-display font-bold text-lg uppercase">Training</h3>
                </div>
                <ul className="space-y-2 text-muted-foreground">
                  <li>• Based in Medicine Hat, Alberta</li>
                  <li>• Winter Training in Arizona</li>
                  <li>• Year-Round Competition Schedule</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Action Gallery */}
      <section className="py-16 bg-background">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display font-bold text-2xl md:text-4xl tracking-tight uppercase text-foreground mb-8 text-center">
            In the Arena
          </h2>
          
          <div className="grid md:grid-cols-3 gap-4">
            <div className="aspect-square rounded-xl overflow-hidden shadow-lg">
              <img 
                src="https://customer-assets.emergentagent.com/job_arena-coach-1/artifacts/ggmmkv1t_473572467_10170600862795055_5342326525834406037_n.jpg" 
                alt="Trent Tunke competing at Strathmore Pro Rodeo 2015"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
              />
            </div>
            <div className="aspect-square rounded-xl overflow-hidden shadow-lg">
              <img 
                src="https://customer-assets.emergentagent.com/job_arena-coach-1/artifacts/ni70vkke_53877561_10161448461090055_8550487812762238976_n.jpg" 
                alt="Trent Tunke competing at Tucson Rodeo"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
              />
            </div>
            <div className="aspect-square rounded-xl overflow-hidden shadow-lg">
              <img 
                src="https://customer-assets.emergentagent.com/job_arena-coach-1/artifacts/cx8ya1lt_Tucson%20Rodeo.jpg" 
                alt="Trent Tunke at La Fiesta de los Vaqueros Tucson Rodeo"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
              />
            </div>
          </div>
          
          <p className="text-center text-muted-foreground mt-6">
            Competing at major rodeos across North America including Strathmore Pro Rodeo and the Tucson Rodeo.
          </p>
        </div>
      </section>

      {/* Quote */}
      <section className="py-16 bg-background">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <blockquote className="font-serif text-2xl md:text-3xl text-foreground italic leading-relaxed">
            "My goal is to help ropers progress at every stage of their journey—whether you're just starting out or pushing for the next level."
          </blockquote>
          <p className="mt-6 font-display font-bold uppercase tracking-wide text-primary">
            — Trent Tunke
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display font-bold text-2xl md:text-4xl tracking-tight uppercase text-foreground mb-4">
            Ready to Get Better?
          </h2>
          <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
            Submit a video of your roping and get detailed feedback from a coach who's been there—and is still competing at the highest level.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signup">
              <Button 
                size="lg" 
                className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-10 py-6 font-bold uppercase tracking-wider"
              >
                Start Your Subscription
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/#pricing">
              <Button 
                variant="outline"
                size="lg" 
                className="rounded-full px-10 py-6 font-bold uppercase tracking-wide border-2"
              >
                View Pricing
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Elevated Roping Media */}
      <section className="py-12 bg-background border-t border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-muted-foreground uppercase tracking-wider mb-3">A platform by</p>
          <h3 className="font-display font-bold text-2xl uppercase tracking-wide text-foreground mb-4">
            Elevated Roping Media
          </h3>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Our flagship online coaching platform from Elevated Roping Media Inc. 
            We're dedicated to advancing the sport through quality instruction, authentic content, 
            and tools that help ropers improve—whether you're working on fundamentals or competing at the highest level.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-foreground text-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_arena-coach-1/artifacts/zh1vb354_Elevated%20Logo.png" 
                alt="Elevated Roping Media" 
                className="h-8 w-auto"
              />
              <span className="font-display font-bold tracking-tight text-background/90">ELEVATED ROPING MEDIA</span>
            </Link>
            <p className="text-background/60 text-xs">
              © {new Date().getFullYear()} Elevated Roping Media Inc. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
